import React, { useState, useRef, useEffect } from "react";
import { X, GripVertical, Trash2, Check, Pin, PinOff } from "lucide-react";
import { truncateText } from "@/utils/utilFunctions/truncateText";
import { PrioritySectionEntry } from "./DenmarkEditContent";
import {
  DenmarkPublish as DenmarkPublishInterface,
  ArticleInfoList,
} from "@/database";
import { GetArticleInfoListApi } from "@/database/media/api";
import PriorityPreviewInline, {
  PreviewSaveStatus,
} from "./PriorityPreviewModal";
import PriorityReviewInfoPopover from "./PriorityReviewInfoPopover";

/** Display-only shape used for rendering the priority list rows. */
interface PriorityItem {
  id: number;
  title: string;
  addedAt?: string;
  views?: number | null;
  comments?: number | null;
  isCurrent?: boolean;
  /**
   * Reference to the untouched original API object.
   * null for a newly injected current-media item (not yet in the API list).
   */
  _raw: any | null;
  /** hostId for newly injected items (_raw === null) that have no API record yet. */
  _hostId?: number;
  /** True when user has marked this row for deletion (faded, sent in deletedList). */
  isDeleted?: boolean;
  /** True when user has pinned this item — position is locked, drag is disabled. */
  isPinned?: boolean;
}

interface PriorityPanelProps {
  selectedChipId: number | string;
  selectedChipTitle: string;
  hostId: number | string;
  onClose: () => void;
  prioritySectionsList: PrioritySectionEntry[];
  activePrioritySectionId: number | null;
  activePrioritySectionEntry: PrioritySectionEntry | null;
  onSectionChipClick: (sectionId: number) => void;
  onRemoveSectionChip: (sectionId: number) => void;
  denmarkPublishData: DenmarkPublishInterface;
  onItemsReorder: (
    sectionId: number,
    orderedItems: any[],
    newlyAddedList?: any[],
    deletedList?: any[]
  ) => void;
  /** Controlled toggle state from parent modal */
  reviewMode: boolean;
  onReviewModeChange: (val: boolean) => void;
  /** Triggers the inline preview (controlled by parent footer button) */
  showPreview: boolean;
  /** Section IDs that were saved successfully (show green tick on chip) */
  savedSectionIds?: Set<number>;
  /** True while a priority list is being fetched for any section */
  isFetchingPriority?: boolean;
  /** When true, only allow reorder & delete — do NOT inject the current media item */
  priorityOnlyMode?: boolean;
  /** Per-section save statuses for inline preview */
  previewSaveStatuses?: Record<number, PreviewSaveStatus>;
  singleCurrentUser: any;
}

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, "");

const formatEpoch = (epoch: number): string => {
  if (!epoch) return "—";
  const d = new Date(epoch);
  const hours = d.getHours().toString().padStart(2, "0");
  const mins = d.getMinutes().toString().padStart(2, "0");
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  return `${hours}:${mins}, ${day} ${month}`;
};

/** Returns true only when the API explicitly says a priority list exists. */
const hasPriorityList = (priorityList?: any): boolean =>
  priorityList?.hasPriority === true;

/**
 * Convert display items back to the original API shape.
 * Existing items: return their untouched _raw object.
 * Newly injected current-media items (_raw === null): build the minimal shape
 * using only the fields the API expects.
 */
const toApiShape = (items: PriorityItem[], pinnedBy: string): any[] =>
  items.map((item) => {
    const base =
      item._raw !== null
        ? item._raw
        : {
            msid: item.id,
            priorityTitle: item.title,
            addedAtTimestampEpoch: Date.now(),
            hostId: item._hostId,
            readonly: undefined,
          };

    if (item.isPinned) {
      // Preserve existing pin or create a new one
      const existingPin = base.priorityPin;
      const pinData =
        existingPin ??
        (() => {
          const now = Date.now();
          return {
            position: items.filter((i) => !i.isDeleted).indexOf(item) + 1,
            createdAt: now,
            expiryAt: now + 24 * 60 * 60 * 1000,
            pinnedBy,
          };
        })();
      return { ...base, priorityPin: pinData };
    }

    // Unpinned — strip priorityPin if it was there
    const { priorityPin: _removed, ...rest } = base;
    return rest;
  });

const buildItems = (
  msid: number | string,
  selectedChipTitle: string,
  hostId: number | string,
  priorityList?: any,
  skipInjection = false,
  articleInfoMap: ArticleInfoList = {}
): PriorityItem[] => {
  // No priority list on this section — return empty; caller will show notice.
  if (!hasPriorityList(priorityList)) return [];

  const currentId = Number(msid);
  const sourceList =
    priorityList?.priorityList?.priorityListEntity?.priorityList ?? [];

  const alreadyPresent = sourceList.some((p: any) => p.msid === currentId);

  // Keep the original API object untouched in _raw; display fields are separate.
  const mapped: PriorityItem[] = sourceList.map((p: any) => {
    const info = articleInfoMap[String(p.msid)];
    return {
      _raw: p,
      id: p.msid,
      title: stripHtml(p.priorityTitle),
      addedAt: formatEpoch(p.addedAtTimestampEpoch),
      views: info?.readCount ?? null,
      comments: info?.commentCount ?? null,
      isCurrent: Number(p.msid) === currentId,
      isPinned: !!p.priorityPin,
    };
  });

  if (!skipInjection && !alreadyPresent) {
    const info = articleInfoMap[String(currentId)];
    const current: PriorityItem = {
      _raw: null, // new item — no original API record yet
      _hostId: Number(hostId) || 0,
      id: currentId,
      title: selectedChipTitle,
      addedAt: formatEpoch(Date.now()),
      views: info?.readCount ?? null,
      comments: info?.commentCount ?? null,
      isCurrent: true,
    };
    return [current, ...mapped];
  }

  return mapped;
};

const PriorityPanel: React.FC<PriorityPanelProps> = ({
  selectedChipId,
  selectedChipTitle,
  hostId,
  onClose,
  prioritySectionsList,
  activePrioritySectionId,
  activePrioritySectionEntry,
  onSectionChipClick,
  onRemoveSectionChip,
  onItemsReorder,
  denmarkPublishData,
  reviewMode,
  onReviewModeChange,
  showPreview,
  savedSectionIds,
  isFetchingPriority = false,
  priorityOnlyMode = false,
  previewSaveStatuses = {},
  singleCurrentUser,
}) => {
  const [items, setItems] = useState<PriorityItem[]>([]);
  const itemsRef = useRef<PriorityItem[]>([]);
  const setItemsSync = (next: PriorityItem[]) => {
    itemsRef.current = next;
    setItems(next);
  };
  const [hoveredCloseSectionId, setHoveredCloseSectionId] = useState<
    number | null
  >(null);
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const [articleInfo, setArticleInfo] = useState<ArticleInfoList>({});
  const [movedItemIds, setMovedItemIds] = useState<Set<number>>(new Set());
  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [editingTitleValue, setEditingTitleValue] = useState<string>("");
  const [editingTitleOriginal, setEditingTitleOriginal] = useState<string>("");

  // Fetch article info only when the active section changes (not on reorder)
  const activeSectionId = activePrioritySectionEntry?.sectionId ?? null;
  useEffect(() => {
    if (!activePrioritySectionEntry || activeSectionId == null) {
      setArticleInfo({});
      return;
    }
    const sourceList =
      activePrioritySectionEntry.priorityList?.priorityList?.priorityListEntity
        ?.priorityList ?? [];
    const msidList = sourceList.map((p: any) => Number(p.msid)).filter(Boolean);
    if (msidList.length === 0) {
      setArticleInfo({});
      return;
    }
    const host =
      activePrioritySectionEntry.priorityList?.priorityList?.priorityListEntity
        ?.holder?.hostId ??
      Number(hostId) ??
      0;

    let cancelled = false;
    GetArticleInfoListApi({ host, msidList }).then((response) => {
      if (cancelled) return;
      if (response.status === 200 && response.data) {
        setArticleInfo(response.data);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionId, hostId]);

  // Set to true right before calling onItemsReorder so the effect below
  // skips the redundant rebuild triggered by the parent state update.
  const justReorderedRef = useRef(false);

  // Rebuild items when active section changes (chip switch, new section added).
  // Skip when we just did a local drag-and-drop — items are already correct.
  useEffect(() => {
    if (justReorderedRef.current) {
      justReorderedRef.current = false;
      return;
    }
    if (activePrioritySectionEntry == null) {
      setItemsSync([]);
      setMovedItemIds(new Set());
      setEditingTitleId(null);
      return;
    }

    setMovedItemIds(new Set());
    setEditingTitleId(null);
    const built = buildItems(
      Number(selectedChipId) ?? Number(denmarkPublishData?.published_id ?? 0),
      denmarkPublishData?.title ?? selectedChipTitle,
      denmarkPublishData?.host?.toString() ?? "",
      activePrioritySectionEntry.priorityList,
      priorityOnlyMode,
      articleInfo
    );
    setItemsSync(built);

    // If selectedChipId was not in the raw list, buildItems prepended it.
    // Persist that injection back into prioritySectionsList and also create
    // newlyAddedList for this section.
    // In priorityOnlyMode we never inject, so skip this entirely.
    if (
      !priorityOnlyMode &&
      hasPriorityList(activePrioritySectionEntry.priorityList)
    ) {
      const rawList =
        activePrioritySectionEntry.priorityList?.priorityList
          ?.priorityListEntity?.priorityList ?? [];
      const selectedChipIdInRaw = rawList.some(
        (p: any) => p.msid === Number(selectedChipId)
      );
      if (!selectedChipIdInRaw && built.length > 0) {
        const newlyAddedItem = {
          msid:
            Number(selectedChipId) ??
            Number(denmarkPublishData?.published_id ?? 0),
          hostId: Number(denmarkPublishData?.host ?? 0) ?? hostId,
          priorityTitle: denmarkPublishData?.title ?? selectedChipTitle,
          priorityType: 0,
          addedAtTimestampEpoch: Date.now(),
          readonly: {
            status: "ACTIVE",
            entityType: "MEDIAVIDEO",
            primeCategoryId: 0,
          },
        };
        justReorderedRef.current = true;
        onItemsReorder(
          activePrioritySectionEntry.sectionId,
          toApiShape(built, singleCurrentUser?.email ?? ""),
          [newlyAddedItem]
        );
      }
    }
  }, [
    activePrioritySectionEntry,
    selectedChipId,
    selectedChipTitle,
    articleInfo,
  ]);

  // Drag-and-drop
  const dragIndex = useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragEnter = (index: number) => {
    if (dragIndex.current === null || dragIndex.current === index) return;
    // Use itemsRef so we always read the latest order, not a stale closure snapshot
    const current = itemsRef.current;
    // Cannot drag onto a pinned row, and pinned rows cannot be dragged
    if (current[index]?.isPinned || current[dragIndex.current]?.isPinned)
      return;

    // Build a new order: remove the dragged item and insert at target, then
    // restore all pinned items to their original indices so they never shift.
    const pinnedSlots = new Map<number, PriorityItem>(
      current
        .map((item, i) => [i, item] as [number, PriorityItem])
        .filter(([, item]) => item.isPinned)
    );

    // Work only with non-pinned items to compute the new order
    const nonPinned = current.filter((item) => !item.isPinned);
    const dragNonPinnedIndex = nonPinned.findIndex(
      (item) => item.id === current[dragIndex.current!]?.id
    );
    const targetItem = current[index];
    const targetNonPinnedIndex = nonPinned.findIndex(
      (item) => item.id === targetItem?.id
    );
    if (dragNonPinnedIndex === -1 || targetNonPinnedIndex === -1) return;

    const reorderedNonPinned = [...nonPinned];
    const [moved] = reorderedNonPinned.splice(dragNonPinnedIndex, 1);
    reorderedNonPinned.splice(targetNonPinnedIndex, 0, moved);

    // Reconstruct the full list, slotting pinned items back at their original indices
    const result: PriorityItem[] = [];
    let nonPinnedCursor = 0;
    for (let i = 0; i < current.length; i++) {
      if (pinnedSlots.has(i)) {
        result.push(pinnedSlots.get(i)!);
      } else {
        result.push(reorderedNonPinned[nonPinnedCursor++]);
      }
    }

    // Find where the moved item ended up so dragIndex stays accurate
    const newDragIndex = result.findIndex((item) => item.id === moved.id);
    dragIndex.current = newDragIndex;
    setMovedItemIds((prev) => new Set([...prev, moved.id]));
    setItemsSync(result);
    const effectiveSectionId = activePrioritySectionEntry?.sectionId ?? null;
    if (effectiveSectionId != null) {
      justReorderedRef.current = true;
      onItemsReorder(
        effectiveSectionId,
        toApiShape(result, singleCurrentUser?.email ?? "")
      );
    }
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  const handlePinToggle = (item: PriorityItem) => {
    const effectiveSectionId = activePrioritySectionEntry?.sectionId ?? null;
    if (effectiveSectionId == null) return;

    const now = Date.now();
    const updatedItems = items.map((i) => {
      if (i.id !== item.id) return i;
      if (i.isPinned) {
        // Unpin — remove priorityPin from _raw
        const updatedRaw =
          i._raw !== null
            ? (() => {
                const { priorityPin: _r, ...rest } = i._raw;
                return rest;
              })()
            : null;
        return { ...i, isPinned: false, _raw: updatedRaw };
      } else {
        // Pin — stamp priorityPin into _raw
        const currentPosition =
          items.filter((x) => !x.isDeleted).indexOf(i) + 1;
        const pin = {
          position: currentPosition,
          createdAt: now,
          expiryAt: now + 24 * 60 * 60 * 1000,
          pinnedBy: singleCurrentUser?.email,
        };
        const updatedRaw =
          i._raw !== null ? { ...i._raw, priorityPin: pin } : null;
        return { ...i, isPinned: true, _raw: updatedRaw };
      }
    });

    setItemsSync(updatedItems);
    justReorderedRef.current = true;
    onItemsReorder(
      effectiveSectionId,
      toApiShape(updatedItems, singleCurrentUser?.email ?? ""),
      activePrioritySectionEntry?.newlyAddedList ?? [],
      activePrioritySectionEntry?.deletedList ?? []
    );
  };

  const handleDeleteItem = (item: PriorityItem) => {
    const effectiveSectionId = activePrioritySectionEntry?.sectionId ?? null;
    if (effectiveSectionId == null) return;

    // Mark deleted locally (for the fade effect) — keeps rows visible but dimmed
    const updatedItems = items.map((i) =>
      i.id === item.id ? { ...i, isDeleted: true } : i
    );
    setItemsSync(updatedItems);

    // Non-deleted items become the new ordered priority list
    const activeItems = updatedItems.filter((i) => !i.isDeleted);

    // Items with _raw === null were injected client-side (not on server yet),
    // so they must not appear in deletedList — just drop from newlyAddedList.
    let newlyAddedList = activePrioritySectionEntry?.newlyAddedList ?? [];
    let deletedEntry: any = null;
    if (item._raw !== null) {
      deletedEntry = item._raw;
    } else {
      newlyAddedList = newlyAddedList.filter((n: any) => n.msid !== item.id);
    }

    const prevDeleted = activePrioritySectionEntry?.deletedList ?? [];
    const newDeletedList = deletedEntry
      ? [...prevDeleted, deletedEntry]
      : prevDeleted;

    justReorderedRef.current = true;
    onItemsReorder(
      effectiveSectionId,
      toApiShape(activeItems, singleCurrentUser?.email ?? ""),
      newlyAddedList,
      newDeletedList
    );
  };

  const commitTitleEdit = (itemId: number) => {
    const newTitle = editingTitleValue.trim();
    if (!newTitle) {
      setEditingTitleId(null);
      return;
    }
    const updatedItems = items.map((i) => {
      if (i.id !== itemId) return i;
      // Patch _raw so toApiShape sends the updated title; for new items (_raw===null) only title field matters
      const updatedRaw =
        i._raw !== null ? { ...i._raw, priorityTitle: newTitle } : null;
      return { ...i, title: newTitle, _raw: updatedRaw };
    });
    setItemsSync(updatedItems);
    setEditingTitleId(null);

    const effectiveSectionId = activePrioritySectionEntry?.sectionId ?? null;
    if (effectiveSectionId != null) {
      const activeItems = updatedItems.filter((i) => !i.isDeleted);
      const currentNewlyAddedList =
        activePrioritySectionEntry?.newlyAddedList ?? [];
      // Also patch newlyAddedList title if this item is a newly added one
      const updatedNewlyAddedList = currentNewlyAddedList.map((n: any) =>
        n.msid === itemId ? { ...n, priorityTitle: newTitle } : n
      );
      justReorderedRef.current = true;
      onItemsReorder(
        effectiveSectionId,
        toApiShape(activeItems, singleCurrentUser?.email ?? ""),
        updatedNewlyAddedList,
        activePrioritySectionEntry?.deletedList ?? []
      );
    }
  };

  const hasNoSections = prioritySectionsList.length === 0;

  // When showPreview is true, render the inline preview instead of the normal panel content
  if (showPreview) {
    return (
      <div className="d-flex flex-column border rounded bg-white h-100 overflow-hidden">
        <PriorityPreviewInline
          prioritySectionsList={prioritySectionsList}
          selectedChipId={selectedChipId}
          selectedChipTitle={selectedChipTitle}
          saveStatuses={previewSaveStatuses}
        />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column border rounded bg-white h-100 overflow-hidden">
      {/* Header row */}
      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom flex-wrap flex-shrink-0">
        {/* <span
          className="badge bg-light text-secondary border"
          style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
        >
          ID: {selectedChipId}
        </span> */}
        <span
          className="text-truncate text-muted small flex-grow-1"
          style={{ minWidth: 0 }}
        >
          {priorityOnlyMode ? (
            activePrioritySectionEntry ? (
              <>
                <span
                  className="px-2 py-0 me-2 flex-shrink-0"
                  style={{
                    fontSize: "0.78rem",
                    color: "#555",
                    border: "1px solid currentColor",
                  }}
                >
                  {activePrioritySectionEntry.sectionId}
                </span>
                {` ${truncateText(denmarkPublishData?.folder_path ?? "", 50)} / ${activePrioritySectionEntry.sectionName}`}
              </>
            ) : (
              "Select a section from the left panel"
            )
          ) : (
            <>
              <span
                className="px-2 py-0 me-2 flex-shrink-0"
                style={{
                  fontSize: "0.78rem",
                  color: "#555",
                  border: "1px solid currentColor",
                }}
              >
                ID: {selectedChipId}
              </span>
              {truncateText(
                (denmarkPublishData?.folder_path ?? "").replace(
                  /^\d+\s*\|\s*/,
                  ""
                ),
                50
              )}
            </>
          )}
        </span>
        {!priorityOnlyMode && (
          <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
            <PriorityReviewInfoPopover />
            <span
              className="text-muted text-nowrap"
              style={{ fontSize: "0.78rem" }}
            >
              Priority Review Mode:
            </span>
            <div className="form-check form-switch mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={reviewMode}
                onChange={(e) => onReviewModeChange(e.target.checked)}
              />
            </div>
            {/* <button
              type="button"
              className="btn btn-sm btn-link text-secondary p-0 ms-1"
              onClick={onClose}
            >
              <X size={16} />
            </button> */}
          </div>
        )}
      </div>

      {/* Media title + section chips */}
      <div className="px-3 pt-2 pb-2 border-bottom flex-shrink-0">
        <p
          className="fw-semibold"
          style={{ color: "#1565c0", fontSize: "0.9rem", marginBottom: "15px" }}
        >
          {truncateText(denmarkPublishData?.title ?? selectedChipTitle, 80)}
        </p>

        {/* {hasNoSections ? (
          <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
            <i className="bi bi-arrow-left-circle me-1"></i>
            Click the{" "}
            <span
              className="badge"
              style={{
                backgroundColor: "#e3f2fd",
                color: "#1565c0",
                fontSize: "0.75rem",
              }}
            >
              ▶
            </span>{" "}
            button next to a section to add it to the priority list.
          </p>
        ) : ( */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {prioritySectionsList.map((entry) => {
            const isActive = entry.sectionId === activePrioritySectionId;
            const isSaved = savedSectionIds?.has(entry.sectionId) ?? false;
            const isCloseHovered = hoveredCloseSectionId === entry.sectionId;
            const showTick = isSaved && !isCloseHovered;
            return (
              <div
                key={entry.sectionId}
                onClick={() => onSectionChipClick(entry.sectionId)}
                className="social-chip flex-shrink-0 user-select-none ps-2"
                style={{
                  cursor: "pointer",
                  ...(isActive
                    ? { borderColor: "#50c377", backgroundColor: "#e8f8ed" }
                    : {}),
                }}
              >
                {/* Label */}
                <span
                  className="fw-medium text-truncate"
                  style={{
                    fontSize: "0.78rem",
                    maxWidth: "80px",
                    color: isActive ? "#1a6b31" : undefined,
                  }}
                >
                  {entry.sectionName}
                </span>

                {/* Close / saved-tick slot */}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!showTick) onRemoveSectionChip(entry.sectionId);
                  }}
                  onMouseEnter={() =>
                    isSaved && setHoveredCloseSectionId(entry.sectionId)
                  }
                  onMouseLeave={() => isSaved && setHoveredCloseSectionId(null)}
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ cursor: showTick ? "default" : "pointer" }}
                >
                  {showTick ? (
                    <i
                      className="bi bi-check-lg text-success"
                      style={{ fontSize: "11px" }}
                    />
                  ) : (
                    <i
                      className="bi bi-x"
                      style={{ fontSize: "14px", paddingTop: "2px" }}
                    />
                  )}
                </span>
              </div>
            );
          })}
        </div>
        {/* )} */}
      </div>

      {/* Full-area loader while fetching */}
      {isFetchingPriority && (
        <div
          className="flex-grow-1 d-flex flex-column align-items-center justify-content-center gap-3"
          style={{ color: "#1565c0" }}
        >
          <div
            className="spinner-border"
            role="status"
            style={{ width: "2.2rem", height: "2.2rem", borderWidth: "3px" }}
          >
            <span className="visually-hidden">Loading…</span>
          </div>
          <span className="text-muted small">Fetching priority list…</span>
        </div>
      )}

      {/* Content area: no-priority notice OR draggable list */}
      {!isFetchingPriority && !hasNoSections && activePrioritySectionEntry && (
        <>
          {!hasPriorityList(activePrioritySectionEntry.priorityList) ? (
            /* ── Section has no priority list ── */
            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center gap-3 px-4 text-center">
              <i
                className="bi bi-exclamation-circle"
                style={{ fontSize: "2.2rem", color: "#e0a800" }}
              />
              <p
                className="mb-0 fw-semibold"
                style={{ color: "#5a4200", fontSize: "0.9rem" }}
              >
                Priority list for this section is not present.
              </p>
              <p className="mb-0 text-muted" style={{ fontSize: "0.82rem" }}>
                Please create one from the{" "}
                <span className="fw-semibold" style={{ color: "#1565c0" }}>
                  Denmark platform
                </span>
                .
              </p>
            </div>
          ) : (
            /* ── Section has a priority list ── */
            <>
              {/* Table header */}
              <div
                className="d-flex align-items-center px-3 py-2 border-bottom bg-light flex-shrink-0 fw-semibold"
                style={{ fontSize: "0.78rem", color: "#1565c0" }}
              >
                <span style={{ width: "17px", marginRight: "4px" }} />
                <span style={{ width: "20px" }}>#</span>
                <span style={{ width: "18px", marginRight: "4px" }} />
                <span style={{ width: "82px" }}>ID</span>
                <span className="flex-grow-1">Priority Title</span>
                <span style={{ width: "110px" }}>Added At</span>
                <span style={{ width: "100px", textAlign: "right" }}>
                  Performance{" "}
                  <i
                    className="bi bi-arrow-repeat"
                    style={{ fontSize: "0.75rem" }}
                  ></i>
                </span>
                <span style={{ width: "28px" }} />
              </div>

              {/* Draggable rows */}
              <div className="overflow-auto flex-grow-1">
                {items.length === 0 ? (
                  <div
                    className="d-flex align-items-center justify-content-center h-100 text-muted"
                    style={{ fontSize: "0.85rem" }}
                  >
                    No priority items for this section yet.
                  </div>
                ) : (
                  items.map((item, index) => (
                    <div
                      key={item.id}
                      draggable={!item.isDeleted && !item.isPinned}
                      onDragStart={() =>
                        !item.isDeleted &&
                        !item.isPinned &&
                        handleDragStart(index)
                      }
                      onDragEnter={() =>
                        !item.isDeleted &&
                        !item.isPinned &&
                        handleDragEnter(index)
                      }
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                      onMouseEnter={() =>
                        !item.isDeleted && setHoveredRowId(item.id)
                      }
                      onMouseLeave={() => setHoveredRowId(null)}
                      className="d-flex align-items-center px-3 py-3 border-bottom user-select-none"
                      style={{
                        fontSize: "0.82rem",
                        backgroundColor: item.isPinned
                          ? "#f0f4ff"
                          : item.isCurrent || movedItemIds.has(item.id)
                            ? "#fffde7"
                            : "#fff",
                        cursor:
                          item.isDeleted || item.isPinned ? "default" : "grab",
                        opacity: item.isDeleted ? 0.35 : 1,
                        transition: "opacity 0.3s ease",
                        pointerEvents: item.isDeleted ? "none" : "auto",
                        borderLeft: item.isPinned
                          ? "3px solid #1565c0"
                          : undefined,
                      }}
                    >
                      <GripVertical
                        size={13}
                        className="me-1 flex-shrink-0"
                        style={{
                          color: item.isPinned ? "transparent" : "#adb5bd",
                        }}
                      />
                      <span
                        className="text-secondary flex-shrink-0"
                        style={{ width: "20px" }}
                      >
                        {index + 1}
                      </span>
                      {/* Pin button — after row number */}
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePinToggle(item);
                        }}
                        title={
                          item.isPinned
                            ? "Unpin item"
                            : "Pin item (locks position)"
                        }
                        className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-1"
                        style={{
                          width: "18px",
                          opacity:
                            item.isPinned || hoveredRowId === item.id ? 1 : 0,
                          transition: "opacity 0.15s ease",
                          cursor: "pointer",
                          color: item.isPinned ? "#1565c0" : "#6c757d",
                        }}
                      >
                        {item.isPinned ? (
                          <Pin size={12} />
                        ) : (
                          <PinOff size={12} />
                        )}
                      </span>
                      <span
                        style={{
                          width: "82px",
                          color: "#1565c0",
                          fontSize: "0.78rem",
                          flexShrink: 0,
                        }}
                      >
                        {truncateText(String(item.id), 9)}
                      </span>
                      {/* Priority Title — inline editable */}
                      <span
                        className="flex-grow-1 d-flex align-items-center pe-2"
                        style={{ minWidth: 0 }}
                      >
                        {editingTitleId === item.id ? (
                          <>
                            <input
                              autoFocus
                              value={editingTitleValue}
                              onChange={(e) =>
                                setEditingTitleValue(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitTitleEdit(item.id);
                                if (e.key === "Escape") setEditingTitleId(null);
                              }}
                              onBlur={() => commitTitleEdit(item.id)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                flex: 1,
                                fontSize: "0.82rem",
                                border: "1px solid #1565c0",
                                borderRadius: "4px",
                                padding: "1px 6px",
                                outline: "none",
                                color: "#212529",
                                minWidth: 0,
                              }}
                            />
                            <span
                              onMouseDown={(e) => {
                                e.preventDefault();
                                commitTitleEdit(item.id);
                              }}
                              title="Save title"
                              className="d-inline-flex align-items-center justify-content-center ms-1 flex-shrink-0 text-success"
                              style={{ cursor: "pointer" }}
                            >
                              <Check size={13} />
                            </span>
                            <span
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setEditingTitleValue(editingTitleOriginal);
                                setEditingTitleId(null);
                              }}
                              title="Cancel edit"
                              className="d-inline-flex align-items-center justify-content-center ms-1 flex-shrink-0 text-danger"
                              style={{ cursor: "pointer" }}
                            >
                              <X size={13} />
                            </span>
                          </>
                        ) : (
                          <span
                            className="text-truncate"
                            title="Click to edit title"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTitleId(item.id);
                              setEditingTitleValue(item.title);
                              setEditingTitleOriginal(item.title);
                            }}
                            style={{
                              color: item.isCurrent ? "#1565c0" : "#212529",
                              cursor: "text",
                            }}
                          >
                            {truncateText(item.title, 60)}
                          </span>
                        )}
                      </span>
                      <span
                        className="text-secondary flex-shrink-0"
                        style={{ width: "110px", fontSize: "0.78rem" }}
                      >
                        {item.addedAt ?? "—"}
                      </span>
                      <span
                        style={{ width: "100px", flexShrink: 0 }}
                        className="d-flex align-items-center justify-content-end gap-2"
                      >
                        {item.views != null ? (
                          <>
                            <span className="d-flex align-items-center gap-1">
                              <i
                                className="bi bi-eye"
                                style={{ fontSize: "0.72rem" }}
                              ></i>
                              {item.views >= 1000
                                ? `${(item.views / 1000).toFixed(2)}K`
                                : item.views}
                            </span>
                            <span className="d-flex align-items-center gap-1">
                              <i
                                className="bi bi-chat"
                                style={{ fontSize: "0.72rem" }}
                              ></i>
                              {item.comments}
                            </span>
                          </>
                        ) : (
                          <span className="text-secondary">NA &nbsp; NA</span>
                        )}
                      </span>

                      {/* Delete: not for the media whose priority is being set */}
                      {item.isCurrent ? (
                        <span
                          className="flex-shrink-0"
                          style={{ width: "28px" }}
                          aria-hidden
                        />
                      ) : (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteItem(item);
                          }}
                          title="Remove from priority list"
                          className="d-inline-flex align-items-center justify-content-center flex-shrink-0 text-danger"
                          style={{
                            width: "28px",
                            opacity: hoveredRowId === item.id ? 1 : 0,
                            transition: "opacity 0.15s ease",
                            cursor: "pointer",
                            pointerEvents:
                              hoveredRowId === item.id ? "auto" : "none",
                          }}
                        >
                          <Trash2 size={13} />
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Empty state when no section chips are present */}
      {!isFetchingPriority && hasNoSections && (
        <div
          className="flex-grow-1 d-flex align-items-center justify-content-center text-muted"
          style={{ fontSize: "0.85rem" }}
        >
          <span>
            Hover on section and click on{" "}
            <span
              style={{
                border: "1px solid #adb5bd",
                borderRadius: "4px",
                padding: "2px 8px",
                fontSize: "0.8rem",
                whiteSpace: "nowrap",
              }}
            >
              + Add
            </span>{" "}
            to load priority.
          </span>
        </div>
      )}
    </div>
  );
};

export default PriorityPanel;
